// Libellés des composants partagés (`packages/ui` : aperçu CV/lettre, panneau
// de diff…), fr + en depuis le 2026-08-26 (chantier i18n, Phase 1), nl depuis
// le 2026-10-16 (issue #137).
//
// `LABELS_FR` est généré depuis le dictionnaire français de l'extension : les
// deux apps disent donc exactement la même chose, ce qui est le but du
// partage. `LABELS_EN` n'est PAS une simple recopie de l'anglais de
// l'extension : le vocabulaire du produit diverge sur deux points (« CV »,
// jamais « resume » ; « Bold », pas « Daring », pour le ciblage Audacieux,
// cf. `i18n.ts`), et le suivre à la lettre aurait réintroduit l'incohérence
// que ce chantier corrige ailleurs. Une clé anglaise malgré tout absente
// retombe sur le français plutôt que sur elle-même : un aperçu à moitié
// anglais vaut mieux qu'un aperçu qui affiche `preview.close` au lieu de
// fermer.

import {
  dictTranslate,
  SCORE_LABELS,
  COMPARE_LABELS,
  type TranslateFn,
  type ScoreLabels,
  type CompareLabels,
} from '@ai-job-fit/ui'
import { useLang, type Lang } from './lang'

const LABELS_FR: Record<string, string> = {
  'error.network': 'Impossible de joindre le service. Si vous êtes sur le réseau ou le VPN de votre entreprise, son filtrage web bloque probablement le domaine. Testez depuis une connexion personnelle (partage 4G, Wi-Fi domestique) pour confirmer.',
  'loader.adapt.1': 'Lecture attentive de l\'offre…',
  'loader.adapt.10': 'Derniers ajustements du CV…',
  'loader.adapt.2': 'Repérage des mots-clés qui comptent…',
  'loader.adapt.3': 'Analyse des attentes du recruteur…',
  'loader.adapt.4': 'Tri de vos expériences les plus pertinentes…',
  'loader.adapt.5': 'Réécriture de vos expériences…',
  'loader.adapt.6': 'Alignement de vos compétences sur l\'offre…',
  'loader.adapt.7': 'Ajustement du vocabulaire au secteur…',
  'loader.adapt.8': 'Mise en avant de vos résultats chiffrés…',
  'loader.adapt.9': 'Optimisation pour les filtres ATS…',
  'loader.letter.1': 'Relecture de votre parcours…',
  'loader.letter.10': 'Relecture finale de la lettre…',
  'loader.letter.2': 'Analyse du ton de l\'entreprise…',
  'loader.letter.3': 'Sélection de vos réalisations les plus convaincantes…',
  'loader.letter.4': 'Recherche du bon angle d\'accroche…',
  'loader.letter.5': 'Rédaction de l\'introduction…',
  'loader.letter.6': 'Mise en récit de votre expérience…',
  'loader.letter.7': 'Connexion entre votre profil et le poste…',
  'loader.letter.8': 'Ajustement du ton et du style…',
  'loader.letter.9': 'Peaufinage de la conclusion…',
  'loader.score.1': 'Lecture de l\'offre…',
  'loader.score.10': 'Calcul du score final…',
  'loader.score.2': 'Extraction des exigences clés…',
  'loader.score.3': 'Repérage des critères éliminatoires…',
  'loader.score.4': 'Comparaison avec votre profil…',
  'loader.score.5': 'Évaluation de vos compétences techniques…',
  'loader.score.6': 'Analyse de votre expérience…',
  'loader.score.7': 'Mesure des écarts à combler…',
  'loader.score.8': 'Repérage de vos atouts…',
  'loader.score.9': 'Pondération des différentes dimensions…',
  'preview.close': 'Fermer',
  'audit.fidelity': 'Fidélité {n} %',
  'audit.translated': "(dossier traduit : les phrases ne s'apparient plus, la fidélité est sous-estimée)",
  'preview.aria': 'Prévisualisation du CV adapté',
  'preview.save.unsaved': 'Modifications non enregistrées : Word et PDF attendent.',
  'preview.save.title': 'Enregistrer vos retouches dans le dossier',
  'preview.save.nothing': 'Aucune modification à enregistrer',
  'preview.save.saving': 'Enregistrement…',
  'preview.save': 'Enregistrer',
  'preview.save.done': 'Enregistré',
  'preview.save.first': "Enregistrez d'abord vos modifications",
  'preview.exportWord.title': 'Mise en page simplifiée, éditable dans Word',
  'preview.exportPdf.title': "Ouvre l'impression du navigateur, puis Enregistrer en PDF",
  'preview.diff.summary': 'Accroche réécrite',
  'preview.diff.skills': 'Compétences modifiées : {n}',
  'preview.diff.counter': '{reworded} reformulées · {added} ajoutées · {removed} retirées · {reordered} réordonnées',
  'preview.diff.fidelity.help': 'Part des puces de votre profil réel conservées (reformulées ou non) dans le CV adapté. 100 % = rien n’a été inventé ni perdu.',
  'preview.diff.hide': 'Masquer les changements',
  'preview.diff.none': 'Aucun changement détecté par rapport à votre profil.',
  'preview.diff.show': 'Voir les changements',
  'preview.editHint.cv': 'Cliquez dans le CV pour le modifier avant d\'exporter. Le PDF sera identique à cet aperçu.',
  'preview.editHint.letter': 'Cliquez dans la lettre pour la modifier avant d\'exporter.',
  'preview.editHint.ok': 'Compris',
  'preview.exportPdf': 'Télécharger PDF',
  'preview.exportWord': 'Word',
  'preview.exportWord.standard': 'Word, mise en page standard',
  'preview.exporting': 'Export…',
  'preview.fidelity.of': 'de votre profil',
  'preview.generateLetter': 'Générer la lettre',
  'preview.generatingLetter': 'Génération…',
  'preview.letter.empty': 'Pas encore de lettre générée.',
  'preview.letter.refine.button': 'Ajuster',
  'preview.cv.refine.placeholder': 'Que changer ? (ex : insister sur mon rôle de PO, plus concis…)',
  'preview.cv.refine.button': 'Refaire le CV',
  'preview.questions.button': 'Questions',
  'preview.questions.facts': 'Pris en compte : {n} précisions',
  'preview.questions.facts.one': 'Pris en compte : 1 précision',
  'preview.changes.translated': 'Traduit en {lang}.',
  'preview.changes.lang.fr': 'français',
  'preview.changes.lang.en': 'anglais',
  'preview.changes.lang.nl': 'néerlandais',
  'preview.changes.lang.de': 'allemand',
  'preview.changes.reworded': '{n} phrases réécrites',
  'preview.changes.reworded.one': '1 phrase réécrite',
  'preview.changes.added': '{n} ajoutées',
  'preview.changes.added.one': '1 ajoutée',
  'preview.changes.removed': '{n} retirées',
  'preview.changes.removed.one': '1 retirée',
  'preview.changes.reordered': '{n} déplacées',
  'preview.changes.reordered.one': '1 déplacée',
  'preview.changes.summary': 'accroche réécrite',
  'preview.changes.skills': '{n} compétences modifiées',
  'preview.changes.skills.one': '1 compétence modifiée',
  'preview.changes.unpaired': 'Les phrases traduites ne se comparent pas une à une : relisez les changements.',
  'preview.changes.allKept': 'Toutes les lignes de votre CV sont là.',
  'preview.changes.lost': "{n} lignes de votre CV sur {total} n'y sont plus.",
  'preview.changes.lost.one': "1 ligne de votre CV sur {total} n'y est plus.",
  'preview.questions.none': 'Aucune précision pour l’instant.',
  'preview.numbers.warning': 'À vérifier avant d’envoyer, absent du profil :',
  'letterAngles.title': 'Avant d’écrire la lettre',
  'letterAngles.intro': 'Choisissez un angle, ou dites-le avec vos mots.',
  'letterAngles.placeholder': 'Avec vos mots (facultatif)',
  'letterAngles.write': 'Écrire la lettre',
  'letterAngles.skip': 'Passer',
  'preview.letter.refine.placeholder': 'Que changer ? (ex : plus courte, plus chaleureuse, insister sur…)',
  'preview.letter.stale': 'Cette lettre a été écrite pour une version précédente du CV.',
  'preview.letter.stale.regenerate': 'Réécrire la lettre',
  'preview.letter.stale.keep': 'La garder',
  'preview.settings.makeDefault': 'En faire mon réglage par défaut',
  'preview.letter.writing': 'Rédaction de la lettre en cours…',
  'preview.rate.cta': 'Noter l\'extension',
  'preview.rate.title': 'Ce document vous aide ? Une note sur le Chrome Web Store nous aide énormément.',
  'preview.regenerateLetter': 'Régénérer la lettre',
  'preview.regenerating': 'Régénération…',
  'preview.settings.accent': 'Couleur d\'accent',
  'preview.settings.apply': 'Refaire le CV avec ces réglages',
  'preview.settings.applyLetter': 'Régénérer la lettre',
  'preview.settings.group.layout': 'Mise en page',
  'preview.settings.group.writing': 'Rédaction IA',
  'preview.settings.hint': 'Mise en page appliquée en direct. Les réglages de rédaction nécessitent une régénération.',
  'preview.settings.language': 'Langue',
  'preview.settings.language.auto': 'Auto · anglais par défaut',
  'preview.settings.language.en': 'Anglais',
  'preview.settings.language.fr': 'Français',
  'preview.settings.language.hint': 'Auto : anglais par défaut ; si l’offre est dans une langue que vous parlez (profil), le document sort dans cette langue.',
  'preview.settings.length': 'Longueur',
  'preview.settings.length.1 page': '1 page',
  'preview.settings.length.2 pages': '2 pages',
  'preview.settings.length.Courte': 'Courte',
  'preview.settings.length.Détaillée': 'Détaillée',
  'preview.settings.length.Sans limite': 'Sans limite',
  'preview.settings.length.Standard': 'Standard',
  'preview.settings.letterHeader': 'En-tête de la lettre',
  'preview.settings.letterHeader.name': 'Nom en titre',
  'preview.settings.letterHeader.none': 'Aucun',
  'preview.settings.letterHeader.sender': 'Expéditeur',
  'preview.settings.opt.audacieux.confirm': 'Mode Audacieux : l’IA pourra revendiquer, de façon plausible, des compétences que vous ne possédez pas encore, à vous de l’assumer en entretien.',
  'preview.settings.opt.audacieux.no': 'Annuler',
  'preview.settings.opt.audacieux.yes': 'Je comprends, activer',
  'preview.settings.opt.Audacieux': 'Audacieux',
  'preview.settings.opt.Ciblé': 'Ciblé',
  'preview.settings.opt.Équilibré': 'Équilibré',
  'preview.settings.opt.Fidèle': 'Fidèle',
  'preview.settings.opt.hint.Audacieux': 'Peut affirmer des compétences que vous n’avez pas encore, à vous de l’assumer en entretien.',
  'preview.settings.opt.hint.Ciblé': 'Priorise et raccourcit pour coller à l’offre, sans rien inventer.',
  'preview.settings.opt.hint.Équilibré': 'Réécrit et raccourcit modérément les points les moins pertinents.',
  'preview.settings.opt.hint.Fidèle': 'Garde tout votre contenu, réordonne pour l’offre. Aucune perte.',
  'preview.settings.optimization': 'Niveau d’adaptation',
  'preview.settings.section.education': 'Formation',
  'preview.settings.section.languages': 'Langues',
  'preview.settings.section.projects': 'Projets',
  'preview.settings.section.skills': 'Compétences',
  'preview.settings.section.summary': 'Phrase d’intro',
  'preview.settings.sections': 'Sections à inclure',
  'preview.settings.template': 'Modèle',
  'preview.settings.template.ats': 'ATS',
  'preview.settings.template.modern': 'Moderne',
  'preview.settings.template.default': 'Toujours utiliser ce modèle',
  'preview.settings.title': 'Réglages',
  'preview.settings.toggle': 'Réglages',
  'preview.settings.tone': 'Ton',
  'preview.settings.tone.Dynamique': 'Dynamique',
  'preview.settings.tone.Exécutif': 'Exécutif',
  'preview.settings.tone.hint.Dynamique': 'Verbes d’action, énergie, résultats mis en avant.',
  'preview.settings.tone.hint.Exécutif': 'Vocabulaire de leadership, pilotage, vision stratégique.',
  'preview.settings.tone.hint.Sobre': 'Phrases directes et factuelles, sans emphase marketing.',
  'preview.settings.tone.Sobre': 'Sobre',
  'preview.tab.cv': 'CV',
  'preview.tab.letter': 'Lettre',
  'preview.versions.current': ' (affichée)',
  'preview.versions.item': 'Version {n} : {label}',
  'preview.versions.label': 'Versions',
  'revision.cancel': 'Fermer',
  'revision.preset.executive': 'Ton exécutif',
  'revision.preset.metrics': 'Mettre les chiffres en avant',
  'revision.preset.punchy': 'Plus percutant',
  'revision.preset.shorter': 'Raccourcir',
  'revision.error.empty': 'L\'IA n\'a pas pu retoucher ce fragment.',
  'revision.placeholder': 'Ou décrivez la retouche…',
  'revision.submit': 'OK',
  'revision.title': 'Retouche IA',
  'revision.trigger': 'Retoucher',
}

/** Une entrée traduite doit reprendre EXACTEMENT les clés de `LABELS_FR` pour
 *  ce libellé, sinon `useLabels` retombe silencieusement sur le français pour
 *  lui. Vocabulaire choisi pour rester cohérent avec `i18n.ts` (« CV », pas
 *  « resume » ; « Bold », pas « Daring » pour le ciblage Audacieux), pas
 *  recopié tel quel depuis l'anglais de l'extension quand les deux divergent. */
const LABELS_EN: Partial<Record<keyof typeof LABELS_FR, string>> = {
  'error.network': "Could not reach the service. If you are on your company network or VPN, its web filtering is most likely blocking the domain: try from a personal connection (phone hotspot, home Wi-Fi) to confirm.",
  'loader.adapt.1': 'Reading the job post closely…',
  'loader.adapt.10': 'Final touches on your CV…',
  'loader.adapt.2': 'Spotting the keywords that matter…',
  'loader.adapt.3': 'Decoding what the recruiter expects…',
  'loader.adapt.4': 'Sorting your most relevant experience…',
  'loader.adapt.5': 'Rewriting your experience…',
  'loader.adapt.6': 'Aligning your skills with the posting…',
  'loader.adapt.7': 'Tuning the wording to the industry…',
  'loader.adapt.8': 'Highlighting your measurable results…',
  'loader.adapt.9': 'Optimising for ATS filters…',
  'loader.letter.1': 'Reviewing your background…',
  'loader.letter.10': 'Final proofread of the letter…',
  'loader.letter.2': "Reading the company's tone…",
  'loader.letter.3': 'Picking your most convincing achievements…',
  'loader.letter.4': 'Finding the right opening angle…',
  'loader.letter.5': 'Writing the introduction…',
  'loader.letter.6': 'Turning your experience into a story…',
  'loader.letter.7': 'Connecting your profile to the role…',
  'loader.letter.8': 'Adjusting tone and style…',
  'loader.letter.9': 'Polishing the closing…',
  'loader.score.1': 'Reading the posting…',
  'loader.score.10': 'Computing the final score…',
  'loader.score.2': 'Extracting the key requirements…',
  'loader.score.3': 'Spotting deal-breaker criteria…',
  'loader.score.4': 'Comparing with your profile…',
  'loader.score.5': 'Assessing your technical skills…',
  'loader.score.6': 'Reviewing your experience…',
  'loader.score.7': 'Measuring the gaps to close…',
  'loader.score.8': 'Identifying your strengths…',
  'loader.score.9': 'Weighing each dimension…',
  'preview.close': 'Close',
  'audit.fidelity': 'Fidelity {n}%',
  'audit.translated': '(translated file: sentences no longer pair up, so fidelity is underestimated)',
  'preview.aria': 'Tailored CV preview',
  'preview.save.unsaved': 'Unsaved changes: Word and PDF are waiting.',
  'preview.save.title': 'Save your edits to the file',
  'preview.save.nothing': 'Nothing to save',
  'preview.save.saving': 'Saving…',
  'preview.save': 'Save',
  'preview.save.done': 'Saved',
  'preview.save.first': 'Save your changes first',
  'preview.exportWord.title': 'Simplified layout, editable in Word',
  'preview.exportPdf.title': 'Opens the browser print dialog, then Save as PDF',
  'preview.diff.summary': 'Summary rewritten',
  'preview.diff.skills': 'Skills changed: {n}',
  'preview.diff.counter': '{reworded} reworded · {added} added · {removed} removed · {reordered} reordered',
  'preview.diff.fidelity.help': 'Share of your real profile bullets kept (reworded or not) in the tailored CV. 100% = nothing invented or lost.',
  'preview.diff.hide': 'Hide changes',
  'preview.diff.none': 'No changes detected versus your profile.',
  'preview.diff.show': 'Show changes',
  'preview.editHint.cv': 'Click inside the CV to edit it before exporting. The PDF will match this preview.',
  'preview.editHint.letter': 'Click inside the letter to edit it before exporting.',
  'preview.editHint.ok': 'Got it',
  'preview.exportPdf': 'Download PDF',
  'preview.exportWord': 'Word',
  'preview.exportWord.standard': 'Word, standard layout',
  'preview.exporting': 'Exporting…',
  'preview.fidelity.of': 'of your profile',
  'preview.generateLetter': 'Generate the letter',
  'preview.generatingLetter': 'Generating…',
  'preview.letter.empty': 'No letter generated yet.',
  'preview.letter.refine.button': 'Adjust',
  'preview.cv.refine.placeholder': 'Say how to steer the CV (e.g. stress my PO role, more concise…)',
  'preview.cv.refine.button': 'Redo the CV',
  'preview.questions.button': 'Questions',
  'preview.questions.facts': 'Taken into account: {n} details',
  'preview.questions.facts.one': 'Taken into account: 1 detail',
  'preview.changes.translated': 'Translated into {lang}.',
  'preview.changes.lang.fr': 'French',
  'preview.changes.lang.en': 'English',
  'preview.changes.lang.nl': 'Dutch',
  'preview.changes.lang.de': 'German',
  'preview.changes.reworded': '{n} sentences rewritten',
  'preview.changes.reworded.one': '1 sentence rewritten',
  'preview.changes.added': '{n} added',
  'preview.changes.added.one': '1 added',
  'preview.changes.removed': '{n} removed',
  'preview.changes.removed.one': '1 removed',
  'preview.changes.reordered': '{n} moved',
  'preview.changes.reordered.one': '1 moved',
  'preview.changes.summary': 'summary rewritten',
  'preview.changes.skills': '{n} skills changed',
  'preview.changes.skills.one': '1 skill changed',
  'preview.changes.unpaired': 'Translated sentences can’t be matched one by one: review the changes.',
  'preview.changes.allKept': 'Every line of your CV is still there.',
  'preview.changes.lost': '{n} of the {total} lines of your CV are gone.',
  'preview.changes.lost.one': '1 of the {total} lines of your CV is gone.',
  'preview.questions.none': 'No details yet.',
  'preview.numbers.warning': 'Check before sending, not in your profile:',
  'letterAngles.title': 'Before writing the letter',
  'letterAngles.intro': 'Pick your angle, or say it in your own words.',
  'letterAngles.placeholder': 'In your own words (optional)',
  'letterAngles.write': 'Write the letter',
  'letterAngles.skip': 'Skip',
  'preview.letter.refine.placeholder': 'Say how to adjust it (e.g. shorter, warmer, insist on…)',
  'preview.letter.stale': 'This letter was written for an earlier version of the CV.',
  'preview.letter.stale.regenerate': 'Rewrite the letter',
  'preview.letter.stale.keep': 'Keep it',
  'preview.settings.makeDefault': 'Make this my default',
  'preview.letter.writing': 'Writing the letter…',
  'preview.rate.cta': 'Rate the extension',
  'preview.rate.title': 'Does this document help? A Chrome Web Store rating helps us a lot.',
  'preview.regenerateLetter': 'Regenerate the letter',
  'preview.regenerating': 'Regenerating…',
  'preview.settings.accent': 'Accent colour',
  'preview.settings.apply': 'Redo the CV with these settings',
  'preview.settings.applyLetter': 'Regenerate the letter',
  'preview.settings.group.layout': 'Layout',
  'preview.settings.group.writing': 'AI writing',
  'preview.settings.hint': 'Layout applies live. Writing settings require a regeneration.',
  'preview.settings.language': 'Language',
  'preview.settings.language.auto': 'Auto · English by default',
  'preview.settings.language.en': 'English',
  'preview.settings.language.fr': 'French',
  'preview.settings.language.hint': 'Auto: English by default; if the posting is in a language you speak (profile), the document comes out in that language.',
  'preview.settings.length': 'Length',
  'preview.settings.length.1 page': '1 page',
  'preview.settings.length.2 pages': '2 pages',
  'preview.settings.length.Courte': 'Short',
  'preview.settings.length.Détaillée': 'Detailed',
  'preview.settings.length.Sans limite': 'No limit',
  'preview.settings.length.Standard': 'Standard',
  'preview.settings.letterHeader': 'Letter header',
  'preview.settings.letterHeader.name': 'Name as title',
  'preview.settings.letterHeader.none': 'None',
  'preview.settings.letterHeader.sender': 'Sender block',
  'preview.settings.opt.audacieux.confirm': "Bold mode: the AI may plausibly claim skills you don't have yet, yours to own at interview.",
  'preview.settings.opt.audacieux.no': 'Cancel',
  'preview.settings.opt.audacieux.yes': 'I understand, enable',
  'preview.settings.opt.Audacieux': 'Bold',
  'preview.settings.opt.Ciblé': 'Targeted',
  'preview.settings.opt.Équilibré': 'Balanced',
  'preview.settings.opt.Fidèle': 'Faithful',
  'preview.settings.opt.hint.Audacieux': "Can claim skills you don't have yet, yours to own at interview.",
  'preview.settings.opt.hint.Ciblé': 'Prioritises and trims to fit the posting, without inventing anything.',
  'preview.settings.opt.hint.Équilibré': 'Rewrites and moderately trims the least relevant points.',
  'preview.settings.opt.hint.Fidèle': 'Keeps all your content, reorders it for the posting. No loss.',
  'preview.settings.optimization': 'Tailoring level',
  'preview.settings.section.education': 'Education',
  'preview.settings.section.languages': 'Languages',
  'preview.settings.section.projects': 'Projects',
  'preview.settings.section.skills': 'Skills',
  'preview.settings.section.summary': 'Intro summary',
  'preview.settings.sections': 'Sections to include',
  'preview.settings.template': 'Template',
  'preview.settings.template.ats': 'ATS',
  'preview.settings.template.modern': 'Modern',
  'preview.settings.template.default': 'Always use this template',
  'preview.settings.title': 'Settings',
  'preview.settings.toggle': 'Settings',
  'preview.settings.tone': 'Tone',
  'preview.settings.tone.Dynamique': 'Dynamic',
  'preview.settings.tone.Exécutif': 'Executive',
  'preview.settings.tone.hint.Dynamique': 'Action verbs, energy, results up front.',
  'preview.settings.tone.hint.Exécutif': 'Leadership wording, ownership, strategic vision.',
  'preview.settings.tone.hint.Sobre': 'Direct, factual sentences, no marketing emphasis.',
  'preview.settings.tone.Sobre': 'Sober',
  'preview.tab.cv': 'CV',
  'preview.tab.letter': 'Letter',
  'preview.versions.current': ' (shown)',
  'preview.versions.item': 'Version {n}: {label}',
  'preview.versions.label': 'Versions',
  'revision.cancel': 'Close',
  'revision.preset.executive': 'Executive tone',
  'revision.preset.metrics': 'Highlight metrics',
  'revision.preset.punchy': 'Punchier',
  'revision.preset.shorter': 'Shorten',
  'revision.error.empty': 'The AI could not revise this fragment.',
  'revision.placeholder': 'Or describe the revision…',
  'revision.submit': 'OK',
  'revision.title': 'AI revision',
  'revision.trigger': 'Revise',
}

/** Néerlandais (issue #137). Repris de l'extension, mais au vouvoiement
 *  (« u »), comme le reste de Radar et de la console. Traduction automatique
 *  pas encore relue : la langue reste cachée tant qu'elle ne l'est pas. */
const LABELS_NL: Record<string, string> = {
  'error.network': 'De dienst kon niet bereikt worden. Als u op het netwerk of de vpn van uw bedrijf zit, blokkeert de webfilter waarschijnlijk het domein. Probeer het via een persoonlijke verbinding (hotspot van uw gsm, wifi thuis) om het te bevestigen.',
  'loader.adapt.1': 'De vacature aandachtig lezen…',
  'loader.adapt.10': 'Laatste aanpassingen aan uw cv…',
  'loader.adapt.2': 'De belangrijke trefwoorden zoeken…',
  'loader.adapt.3': 'De verwachtingen van de recruiter ontleden…',
  'loader.adapt.4': 'Uw meest relevante ervaring selecteren…',
  'loader.adapt.5': 'Uw ervaring herschrijven…',
  'loader.adapt.6': 'Uw vaardigheden afstemmen op de vacature…',
  'loader.adapt.7': 'De woordkeuze afstemmen op de sector…',
  'loader.adapt.8': 'Uw meetbare resultaten in de verf zetten…',
  'loader.adapt.9': 'Optimaliseren voor ATS-filters…',
  'loader.letter.1': 'Uw loopbaan doornemen…',
  'loader.letter.10': 'De brief een laatste keer nalezen…',
  'loader.letter.2': 'De toon van het bedrijf analyseren…',
  'loader.letter.3': 'Uw overtuigendste realisaties kiezen…',
  'loader.letter.4': 'De juiste invalshoek zoeken…',
  'loader.letter.5': 'De inleiding schrijven…',
  'loader.letter.6': 'Uw ervaring tot een verhaal maken…',
  'loader.letter.7': 'Uw profiel aan de functie koppelen…',
  'loader.letter.8': 'Toon en stijl bijstellen…',
  'loader.letter.9': 'Het slot verfijnen…',
  'loader.score.1': 'De vacature lezen…',
  'loader.score.10': 'De eindscore berekenen…',
  'loader.score.2': 'De belangrijkste vereisten halen…',
  'loader.score.3': 'Mogelijke uitsluitingscriteria zoeken…',
  'loader.score.4': 'Vergelijken met uw profiel…',
  'loader.score.5': 'Uw technische vaardigheden beoordelen…',
  'loader.score.6': 'Uw ervaring analyseren…',
  'loader.score.7': 'De te overbruggen verschillen meten…',
  'loader.score.8': 'Uw troeven zoeken…',
  'loader.score.9': 'De verschillende dimensies wegen…',
  'preview.close': 'Sluiten',
  'audit.fidelity': 'Trouw {n} %',
  'audit.translated': '(vertaald dossier: de zinnen zijn niet meer te koppelen, de trouw wordt onderschat)',
  'preview.aria': 'Voorbeeld van het aangepaste cv',
  'preview.save.unsaved': 'Niet-bewaarde wijzigingen: Word en pdf wachten.',
  'preview.save.title': 'Uw aanpassingen in het dossier bewaren',
  'preview.save.nothing': 'Niets te bewaren',
  'preview.save.saving': 'Bewaren…',
  'preview.save': 'Bewaren',
  'preview.save.done': 'Bewaard',
  'preview.save.first': 'Bewaar eerst uw wijzigingen',
  'preview.exportWord.title': 'Vereenvoudigde opmaak, aanpasbaar in Word',
  'preview.exportPdf.title': 'Opent het afdrukvenster van de browser, daarna Opslaan als pdf',
  'preview.diff.summary': 'Samenvatting herschreven',
  'preview.diff.skills': 'Gewijzigde vaardigheden: {n}',
  'preview.diff.counter': '{reworded} herschreven · {added} toegevoegd · {removed} verwijderd · {reordered} verplaatst',
  'preview.diff.fidelity.help': 'Deel van de punten van uw echte profiel die behouden zijn (al dan niet herschreven) in het aangepaste cv. 100 % = niets verzonnen of verloren.',
  'preview.diff.hide': 'Wijzigingen verbergen',
  'preview.diff.none': 'Geen wijzigingen ten opzichte van uw profiel.',
  'preview.diff.show': 'Wijzigingen tonen',
  'preview.editHint.cv': 'Klik in het cv om het aan te passen voor u exporteert. De pdf komt overeen met dit voorbeeld.',
  'preview.editHint.letter': 'Klik in de brief om hem aan te passen voor u exporteert.',
  'preview.editHint.ok': 'Begrepen',
  'preview.exportPdf': 'Pdf downloaden',
  'preview.exportWord': 'Word',
  'preview.exportWord.standard': 'Word, standaardopmaak',
  'preview.exporting': 'Exporteren…',
  'preview.fidelity.of': 'van uw profiel',
  'preview.generateLetter': 'Brief opstellen',
  'preview.generatingLetter': 'Bezig…',
  'preview.letter.empty': 'Nog geen brief opgesteld.',
  'preview.letter.refine.button': 'Bijsturen',
  'preview.cv.refine.placeholder': 'Wat moet er anders? (bv. mijn rol als PO benadrukken, beknopter…)',
  'preview.cv.refine.button': 'Cv opnieuw maken',
  'preview.questions.button': 'Vragen',
  'preview.questions.facts': 'Meegenomen: {n} details',
  'preview.questions.facts.one': 'Meegenomen: 1 detail',
  'preview.changes.translated': 'Vertaald naar het {lang}.',
  'preview.changes.lang.fr': 'Frans',
  'preview.changes.lang.en': 'Engels',
  'preview.changes.lang.nl': 'Nederlands',
  'preview.changes.lang.de': 'Duits',
  'preview.changes.reworded': '{n} zinnen herschreven',
  'preview.changes.reworded.one': '1 zin herschreven',
  'preview.changes.added': '{n} toegevoegd',
  'preview.changes.added.one': '1 toegevoegd',
  'preview.changes.removed': '{n} verwijderd',
  'preview.changes.removed.one': '1 verwijderd',
  'preview.changes.reordered': '{n} verplaatst',
  'preview.changes.reordered.one': '1 verplaatst',
  'preview.changes.summary': 'samenvatting herschreven',
  'preview.changes.skills': '{n} vaardigheden gewijzigd',
  'preview.changes.skills.one': '1 vaardigheid gewijzigd',
  'preview.changes.unpaired': 'Vertaalde zinnen kunnen niet een voor een gekoppeld worden: kijk de wijzigingen na.',
  'preview.changes.allKept': 'Alle regels van uw cv staan er nog.',
  'preview.changes.lost': '{n} van de {total} regels van uw cv staan er niet meer.',
  'preview.changes.lost.one': '1 van de {total} regels van uw cv staat er niet meer.',
  'preview.questions.none': 'Nog geen details.',
  'preview.numbers.warning': 'Na te kijken voor u verstuurt, staat niet in het profiel:',
  'letterAngles.title': 'Voor u de brief schrijft',
  'letterAngles.intro': 'Kies een invalshoek, of zeg het in uw eigen woorden.',
  'letterAngles.placeholder': 'In uw eigen woorden (optioneel)',
  'letterAngles.write': 'De brief schrijven',
  'letterAngles.skip': 'Overslaan',
  'preview.letter.refine.placeholder': 'Wat moet er anders? (bv. korter, warmer, meer nadruk op…)',
  'preview.letter.stale': 'Deze brief is geschreven voor een vorige versie van het cv.',
  'preview.letter.stale.regenerate': 'De brief herschrijven',
  'preview.letter.stale.keep': 'Behouden',
  'preview.settings.makeDefault': 'Als mijn standaard instellen',
  'preview.letter.writing': 'Uw brief wordt geschreven…',
  'preview.rate.cta': 'De extensie beoordelen',
  'preview.rate.title': 'Helpt dit document u? Een beoordeling in de Chrome Web Store helpt ons enorm.',
  'preview.regenerateLetter': 'Brief opnieuw opstellen',
  'preview.regenerating': 'Opnieuw maken…',
  'preview.settings.accent': 'Accentkleur',
  'preview.settings.apply': 'Cv opnieuw maken met deze instellingen',
  'preview.settings.applyLetter': 'Brief opnieuw opstellen',
  'preview.settings.group.layout': 'Opmaak',
  'preview.settings.group.writing': 'Schrijven door AI',
  'preview.settings.hint': 'De opmaak wordt meteen toegepast. De schrijfinstellingen vragen een nieuwe versie.',
  'preview.settings.language': 'Taal',
  'preview.settings.language.auto': 'Auto · standaard Engels',
  'preview.settings.language.en': 'Engels',
  'preview.settings.language.fr': 'Frans',
  'preview.settings.language.hint': 'Auto: standaard Engels; als de vacature in een taal staat die u spreekt (profiel), verschijnt het document in die taal.',
  'preview.settings.length': 'Lengte',
  'preview.settings.length.1 page': '1 pagina',
  'preview.settings.length.2 pages': '2 pagina’s',
  'preview.settings.length.Courte': 'Kort',
  'preview.settings.length.Détaillée': 'Uitgebreid',
  'preview.settings.length.Sans limite': 'Zonder limiet',
  'preview.settings.length.Standard': 'Standaard',
  'preview.settings.letterHeader': 'Hoofding van de brief',
  'preview.settings.letterHeader.name': 'Naam als titel',
  'preview.settings.letterHeader.none': 'Geen',
  'preview.settings.letterHeader.sender': 'Blok afzender',
  'preview.settings.opt.audacieux.confirm': 'Gedurfde modus: de AI kan geloofwaardig vaardigheden claimen die u nog niet hebt, aan u om ze in een gesprek waar te maken.',
  'preview.settings.opt.audacieux.no': 'Annuleren',
  'preview.settings.opt.audacieux.yes': 'Begrepen, inschakelen',
  'preview.settings.opt.Audacieux': 'Gedurfd',
  'preview.settings.opt.Ciblé': 'Gericht',
  'preview.settings.opt.Équilibré': 'Evenwichtig',
  'preview.settings.opt.Fidèle': 'Getrouw',
  'preview.settings.opt.hint.Audacieux': 'Kan vaardigheden claimen die u nog niet hebt, aan u om ze in een gesprek waar te maken.',
  'preview.settings.opt.hint.Ciblé': 'Geeft voorrang en schrapt om bij de vacature te passen, zonder iets te verzinnen.',
  'preview.settings.opt.hint.Équilibré': 'Herschrijft en schrapt matig de minst relevante punten.',
  'preview.settings.opt.hint.Fidèle': 'Behoudt al uw inhoud, herschikt voor de vacature. Geen verlies.',
  'preview.settings.optimization': 'Aanpassingsniveau',
  'preview.settings.section.education': 'Opleiding',
  'preview.settings.section.languages': 'Talen',
  'preview.settings.section.projects': 'Projecten',
  'preview.settings.section.skills': 'Vaardigheden',
  'preview.settings.section.summary': 'Inleidende samenvatting',
  'preview.settings.sections': 'Rubrieken om op te nemen',
  'preview.settings.template': 'Sjabloon',
  'preview.settings.template.ats': 'ATS',
  'preview.settings.template.modern': 'Modern',
  'preview.settings.template.default': 'Dit sjabloon altijd gebruiken',
  'preview.settings.title': 'Instellingen',
  'preview.settings.toggle': 'Instellingen',
  'preview.settings.tone': 'Toon',
  'preview.settings.tone.Dynamique': 'Dynamisch',
  'preview.settings.tone.Exécutif': 'Executive',
  'preview.settings.tone.hint.Dynamique': 'Actiewerkwoorden, energie, resultaten vooraan.',
  'preview.settings.tone.hint.Exécutif': 'Taal van leiderschap, verantwoordelijkheid, strategische visie.',
  'preview.settings.tone.hint.Sobre': 'Directe, feitelijke zinnen, zonder marketingtaal.',
  'preview.settings.tone.Sobre': 'Sober',
  'preview.tab.cv': 'Cv',
  'preview.tab.letter': 'Brief',
  'preview.versions.current': ' (getoond)',
  'preview.versions.item': 'Versie {n}: {label}',
  'preview.versions.label': 'Versies',
  'revision.cancel': 'Sluiten',
  'revision.preset.executive': 'Executive toon',
  'revision.preset.metrics': 'Cijfers benadrukken',
  'revision.preset.punchy': 'Krachtiger',
  'revision.preset.shorter': 'Inkorten',
  'revision.error.empty': 'De AI kon dit fragment niet herwerken.',
  'revision.placeholder': 'Of beschrijf de herwerking…',
  'revision.submit': 'OK',
  'revision.title': 'Herwerking door AI',
  'revision.trigger': 'Herwerken',
}

/** Toujours français, jamais réactif : pour le seul usage qui ne peut pas
 *  appeler de hook, `labels.test.ts` (vérifie la complétude du dictionnaire
 *  hors composant). Les composants React utilisent `useLabels()` ci-dessous,
 *  pas cette fonction. */
export const t = dictTranslate(LABELS_FR)

/** Italien (issue #137), repris de l'extension : les deux tutoient en
 *  italien. Traduction automatique pas encore relue. */
const LABELS_IT: Record<string, string> = {
  'error.network': 'Impossibile raggiungere il servizio. Se sei sulla rete o sulla VPN della tua azienda, il filtro web blocca probabilmente il dominio. Prova da una connessione personale (hotspot del telefono, Wi-Fi di casa) per confermarlo.',
  'loader.adapt.1': 'Lettura attenta dell’offerta…',
  'loader.adapt.10': 'Ultimi ritocchi al tuo CV…',
  'loader.adapt.2': 'Individuazione delle parole chiave che contano…',
  'loader.adapt.3': 'Analisi delle aspettative del selezionatore…',
  'loader.adapt.4': 'Selezione delle tue esperienze più pertinenti…',
  'loader.adapt.5': 'Riscrittura delle tue esperienze…',
  'loader.adapt.6': 'Allineamento delle tue competenze all’offerta…',
  'loader.adapt.7': 'Adattamento del lessico al settore…',
  'loader.adapt.8': 'Messa in evidenza dei tuoi risultati misurabili…',
  'loader.adapt.9': 'Ottimizzazione per i filtri ATS…',
  'loader.letter.1': 'Rilettura del tuo percorso…',
  'loader.letter.10': 'Rilettura finale della lettera…',
  'loader.letter.2': 'Analisi del tono dell’azienda…',
  'loader.letter.3': 'Scelta dei tuoi risultati più convincenti…',
  'loader.letter.4': 'Ricerca dell’attacco giusto…',
  'loader.letter.5': 'Scrittura dell’introduzione…',
  'loader.letter.6': 'La tua esperienza diventa un racconto…',
  'loader.letter.7': 'Collegamento tra il tuo profilo e il ruolo…',
  'loader.letter.8': 'Regolazione di tono e stile…',
  'loader.letter.9': 'Rifinitura della conclusione…',
  'loader.score.1': 'Lettura dell’offerta…',
  'loader.score.10': 'Calcolo del punteggio finale…',
  'loader.score.2': 'Estrazione dei requisiti chiave…',
  'loader.score.3': 'Individuazione dei possibili criteri esclusivi…',
  'loader.score.4': 'Confronto con il tuo profilo…',
  'loader.score.5': 'Valutazione delle tue competenze tecniche…',
  'loader.score.6': 'Analisi della tua esperienza…',
  'loader.score.7': 'Misura delle lacune da colmare…',
  'loader.score.8': 'Individuazione dei tuoi punti di forza…',
  'loader.score.9': 'Ponderazione delle varie dimensioni…',
  'preview.close': 'Chiudi',
  'audit.fidelity': 'Fedeltà {n} %',
  'audit.translated': '(dossier tradotto: le frasi non si possono più abbinare, la fedeltà è sottostimata)',
  'preview.aria': 'Anteprima del CV adattato',
  'preview.save.unsaved': 'Modifiche non salvate: Word e PDF sono in attesa.',
  'preview.save.title': 'Salva le tue modifiche nel documento',
  'preview.save.nothing': 'Niente da salvare',
  'preview.save.saving': 'Salvataggio…',
  'preview.save': 'Salva',
  'preview.save.done': 'Salvato',
  'preview.save.first': 'Salva prima le tue modifiche',
  'preview.exportWord.title': 'Impaginazione semplificata, modificabile in Word',
  'preview.exportPdf.title': 'Apre la stampa del browser, poi Salva come PDF',
  'preview.diff.summary': 'Presentazione riscritta',
  'preview.diff.skills': 'Competenze modificate: {n}',
  'preview.diff.counter': '{reworded} riformulate · {added} aggiunte · {removed} rimosse · {reordered} riordinate',
  'preview.diff.fidelity.help': 'Quota dei punti del tuo profilo reale mantenuti (riformulati o no) nel CV adattato. 100 % = niente inventato né perso.',
  'preview.diff.hide': 'Nascondi le modifiche',
  'preview.diff.none': 'Nessuna modifica rispetto al tuo profilo.',
  'preview.diff.show': 'Mostra le modifiche',
  'preview.editHint.cv': 'Clicca nel CV per modificarlo prima di esportarlo. Il PDF sarà identico a questa anteprima.',
  'preview.editHint.letter': 'Clicca nella lettera per modificarla prima di esportarla.',
  'preview.editHint.ok': 'Capito',
  'preview.exportPdf': 'Scarica il PDF',
  'preview.exportWord': 'Word',
  'preview.exportWord.standard': 'Word, impaginazione standard',
  'preview.exporting': 'Esportazione…',
  'preview.fidelity.of': 'del tuo profilo',
  'preview.generateLetter': 'Genera la lettera',
  'preview.generatingLetter': 'Generazione…',
  'preview.letter.empty': 'Nessuna lettera generata per ora.',
  'preview.letter.refine.button': 'Modifica',
  'preview.cv.refine.placeholder': 'Cosa cambiare? (es. insisti sul mio ruolo di PO, più conciso…)',
  'preview.cv.refine.button': 'Rifai il CV',
  'preview.questions.button': 'Domande',
  'preview.questions.facts': 'Considerati: {n} dettagli',
  'preview.questions.facts.one': 'Considerato: 1 dettaglio',
  'preview.changes.translated': 'Tradotto in {lang}.',
  'preview.changes.lang.fr': 'francese',
  'preview.changes.lang.en': 'inglese',
  'preview.changes.lang.nl': 'olandese',
  'preview.changes.lang.de': 'tedesco',
  'preview.changes.reworded': '{n} frasi riscritte',
  'preview.changes.reworded.one': '1 frase riscritta',
  'preview.changes.added': '{n} aggiunte',
  'preview.changes.added.one': '1 aggiunta',
  'preview.changes.removed': '{n} rimosse',
  'preview.changes.removed.one': '1 rimossa',
  'preview.changes.reordered': '{n} spostate',
  'preview.changes.reordered.one': '1 spostata',
  'preview.changes.summary': 'presentazione riscritta',
  'preview.changes.skills': '{n} competenze modificate',
  'preview.changes.skills.one': '1 competenza modificata',
  'preview.changes.unpaired': 'Le frasi tradotte non si possono confrontare una per una: rileggi le modifiche.',
  'preview.changes.allKept': 'Tutte le righe del tuo CV ci sono ancora.',
  'preview.changes.lost': '{n} righe del tuo CV su {total} non ci sono più.',
  'preview.changes.lost.one': '1 riga del tuo CV su {total} non c’è più.',
  'preview.questions.none': 'Ancora nessun dettaglio.',
  'preview.numbers.warning': 'Da verificare prima di inviare, assente dal profilo:',
  'letterAngles.title': 'Prima di scrivere la lettera',
  'letterAngles.intro': 'Scegli un taglio, o dillo con parole tue.',
  'letterAngles.placeholder': 'Con parole tue (facoltativo)',
  'letterAngles.write': 'Scrivi la lettera',
  'letterAngles.skip': 'Salta',
  'preview.letter.refine.placeholder': 'Cosa cambiare? (es. più breve, più calorosa, insisti su…)',
  'preview.letter.stale': 'Questa lettera è stata scritta per una versione precedente del CV.',
  'preview.letter.stale.regenerate': 'Riscrivi la lettera',
  'preview.letter.stale.keep': 'Tienila',
  'preview.settings.makeDefault': 'Usa come impostazione predefinita',
  'preview.letter.writing': 'Scrittura della lettera in corso…',
  'preview.rate.cta': 'Valuta l’estensione',
  'preview.rate.title': 'Ti è utile? Una valutazione sul Chrome Web Store ci aiuta moltissimo.',
  'preview.regenerateLetter': 'Rigenera la lettera',
  'preview.regenerating': 'Rigenerazione…',
  'preview.settings.accent': 'Colore di accento',
  'preview.settings.apply': 'Rifai il CV con queste impostazioni',
  'preview.settings.applyLetter': 'Rigenera la lettera',
  'preview.settings.group.layout': 'Impaginazione',
  'preview.settings.group.writing': 'Scrittura IA',
  'preview.settings.hint': 'L’impaginazione si applica subito. Le impostazioni di scrittura richiedono una rigenerazione.',
  'preview.settings.language': 'Lingua',
  'preview.settings.language.auto': 'Auto · inglese per impostazione predefinita',
  'preview.settings.language.en': 'Inglese',
  'preview.settings.language.fr': 'Francese',
  'preview.settings.language.hint': 'Auto: inglese per impostazione predefinita; se l’offerta è scritta in una lingua che parli (profilo), il documento esce in quella lingua.',
  'preview.settings.length': 'Lunghezza',
  'preview.settings.length.1 page': '1 pagina',
  'preview.settings.length.2 pages': '2 pagine',
  'preview.settings.length.Courte': 'Breve',
  'preview.settings.length.Détaillée': 'Dettagliata',
  'preview.settings.length.Sans limite': 'Senza limite',
  'preview.settings.length.Standard': 'Standard',
  'preview.settings.letterHeader': 'Intestazione della lettera',
  'preview.settings.letterHeader.name': 'Nome come titolo',
  'preview.settings.letterHeader.none': 'Nessuna',
  'preview.settings.letterHeader.sender': 'Mittente',
  'preview.settings.opt.audacieux.confirm': 'Modalità Audace: l’IA potrà affermare, in modo plausibile, competenze che non hai ancora, sta a te sostenerle al colloquio.',
  'preview.settings.opt.audacieux.no': 'Annulla',
  'preview.settings.opt.audacieux.yes': 'Ho capito, attiva',
  'preview.settings.opt.Audacieux': 'Audace',
  'preview.settings.opt.Ciblé': 'Mirato',
  'preview.settings.opt.Équilibré': 'Equilibrato',
  'preview.settings.opt.Fidèle': 'Fedele',
  'preview.settings.opt.hint.Audacieux': 'Può affermare competenze che non hai ancora, sta a te sostenerle al colloquio.',
  'preview.settings.opt.hint.Ciblé': 'Dà priorità e accorcia per aderire all’offerta, senza inventare nulla.',
  'preview.settings.opt.hint.Équilibré': 'Riscrive e accorcia moderatamente i punti meno pertinenti.',
  'preview.settings.opt.hint.Fidèle': 'Mantiene tutto il tuo contenuto, lo riordina per l’offerta. Nessuna perdita.',
  'preview.settings.optimization': 'Livello di adattamento',
  'preview.settings.section.education': 'Formazione',
  'preview.settings.section.languages': 'Lingue',
  'preview.settings.section.projects': 'Progetti',
  'preview.settings.section.skills': 'Competenze',
  'preview.settings.section.summary': 'Frase di presentazione',
  'preview.settings.sections': 'Sezioni da includere',
  'preview.settings.template': 'Modello',
  'preview.settings.template.ats': 'ATS',
  'preview.settings.template.modern': 'Moderno',
  'preview.settings.template.default': 'Usa sempre questo modello',
  'preview.settings.title': 'Impostazioni',
  'preview.settings.toggle': 'Impostazioni',
  'preview.settings.tone': 'Tono',
  'preview.settings.tone.Dynamique': 'Dinamico',
  'preview.settings.tone.Exécutif': 'Executive',
  'preview.settings.tone.hint.Dynamique': 'Verbi d’azione, energia, risultati in primo piano.',
  'preview.settings.tone.hint.Exécutif': 'Lessico di leadership, guida, visione strategica.',
  'preview.settings.tone.hint.Sobre': 'Frasi dirette e fattuali, senza enfasi da marketing.',
  'preview.settings.tone.Sobre': 'Sobrio',
  'preview.tab.cv': 'CV',
  'preview.tab.letter': 'Lettera',
  'preview.versions.current': ' (mostrata)',
  'preview.versions.item': 'Versione {n}: {label}',
  'preview.versions.label': 'Versioni',
  'revision.cancel': 'Chiudi',
  'revision.preset.executive': 'Tono executive',
  'revision.preset.metrics': 'Metti in risalto i numeri',
  'revision.preset.punchy': 'Più incisivo',
  'revision.preset.shorter': 'Accorcia',
  'revision.error.empty': 'L’IA non è riuscita a rivedere questo frammento.',
  'revision.placeholder': 'Oppure descrivi la revisione…',
  'revision.submit': 'OK',
  'revision.title': 'Revisione con l’IA',
  'revision.trigger': 'Rivedi',
}

/** Espagnol (issue #137), repris de l'extension : les deux tutoient en
 *  espagnol. Traduction automatique pas encore relue. */
const LABELS_ES: Record<string, string> = {
  'error.network': 'No se pudo contactar con el servicio. Si estás en la red o la VPN de tu empresa, su filtro web probablemente bloquea el dominio. Prueba desde una conexión personal (compartir datos del móvil, wifi de casa) para confirmarlo.',
  'loader.adapt.1': 'Leyendo la oferta con atención…',
  'loader.adapt.10': 'Últimos retoques a tu CV…',
  'loader.adapt.2': 'Localizando las palabras clave que importan…',
  'loader.adapt.3': 'Analizando lo que espera el reclutador…',
  'loader.adapt.4': 'Seleccionando tus experiencias más pertinentes…',
  'loader.adapt.5': 'Reescribiendo tus experiencias…',
  'loader.adapt.6': 'Alineando tus competencias con la oferta…',
  'loader.adapt.7': 'Ajustando el vocabulario al sector…',
  'loader.adapt.8': 'Destacando tus resultados medibles…',
  'loader.adapt.9': 'Optimizando para los filtros ATS…',
  'loader.letter.1': 'Repasando tu trayectoria…',
  'loader.letter.10': 'Revisión final de la carta…',
  'loader.letter.2': 'Analizando el tono de la empresa…',
  'loader.letter.3': 'Eligiendo tus logros más convincentes…',
  'loader.letter.4': 'Buscando el enfoque adecuado…',
  'loader.letter.5': 'Escribiendo la introducción…',
  'loader.letter.6': 'Convirtiendo tu experiencia en un relato…',
  'loader.letter.7': 'Conectando tu perfil con el puesto…',
  'loader.letter.8': 'Ajustando tono y estilo…',
  'loader.letter.9': 'Puliendo la conclusión…',
  'loader.score.1': 'Leyendo la oferta…',
  'loader.score.10': 'Calculando la puntuación final…',
  'loader.score.2': 'Extrayendo los requisitos clave…',
  'loader.score.3': 'Buscando posibles criterios excluyentes…',
  'loader.score.4': 'Comparando con tu perfil…',
  'loader.score.5': 'Evaluando tus competencias técnicas…',
  'loader.score.6': 'Analizando tu experiencia…',
  'loader.score.7': 'Midiendo las carencias a cubrir…',
  'loader.score.8': 'Identificando tus puntos fuertes…',
  'loader.score.9': 'Ponderando las distintas dimensiones…',
  'preview.close': 'Cerrar',
  'audit.fidelity': 'Fidelidad {n} %',
  'audit.translated': '(expediente traducido: las frases ya no se pueden emparejar, la fidelidad está infravalorada)',
  'preview.aria': 'Vista previa del CV adaptado',
  'preview.save.unsaved': 'Cambios sin guardar: Word y PDF están esperando.',
  'preview.save.title': 'Guardar tus retoques en el documento',
  'preview.save.nothing': 'Nada que guardar',
  'preview.save.saving': 'Guardando…',
  'preview.save': 'Guardar',
  'preview.save.done': 'Guardado',
  'preview.save.first': 'Guarda primero tus cambios',
  'preview.exportWord.title': 'Maquetación simplificada, editable en Word',
  'preview.exportPdf.title': 'Abre la impresión del navegador, luego Guardar como PDF',
  'preview.diff.summary': 'Presentación reescrita',
  'preview.diff.skills': 'Competencias modificadas: {n}',
  'preview.diff.counter': '{reworded} reformuladas · {added} añadidas · {removed} quitadas · {reordered} reordenadas',
  'preview.diff.fidelity.help': 'Parte de los puntos de tu perfil real conservados (reformulados o no) en el CV adaptado. 100 % = nada inventado ni perdido.',
  'preview.diff.hide': 'Ocultar los cambios',
  'preview.diff.none': 'No se ha detectado ningún cambio respecto a tu perfil.',
  'preview.diff.show': 'Ver los cambios',
  'preview.editHint.cv': 'Haz clic en el CV para modificarlo antes de exportarlo. El PDF será idéntico a esta vista previa.',
  'preview.editHint.letter': 'Haz clic en la carta para modificarla antes de exportarla.',
  'preview.editHint.ok': 'Entendido',
  'preview.exportPdf': 'Descargar PDF',
  'preview.exportWord': 'Word',
  'preview.exportWord.standard': 'Word, diseño estándar',
  'preview.exporting': 'Exportando…',
  'preview.fidelity.of': 'de tu perfil',
  'preview.generateLetter': 'Generar la carta',
  'preview.generatingLetter': 'Generando…',
  'preview.letter.empty': 'Todavía no se ha generado ninguna carta.',
  'preview.letter.refine.button': 'Ajustar',
  'preview.cv.refine.placeholder': '¿Qué cambiar? (p. ej. insiste en mi papel de PO, más conciso…)',
  'preview.cv.refine.button': 'Rehacer el CV',
  'preview.questions.button': 'Preguntas',
  'preview.questions.facts': 'Tenidos en cuenta: {n} detalles',
  'preview.questions.facts.one': 'Tenido en cuenta: 1 detalle',
  'preview.changes.translated': 'Traducido al {lang}.',
  'preview.changes.lang.fr': 'francés',
  'preview.changes.lang.en': 'inglés',
  'preview.changes.lang.nl': 'neerlandés',
  'preview.changes.lang.de': 'alemán',
  'preview.changes.reworded': '{n} frases reescritas',
  'preview.changes.reworded.one': '1 frase reescrita',
  'preview.changes.added': '{n} añadidas',
  'preview.changes.added.one': '1 añadida',
  'preview.changes.removed': '{n} quitadas',
  'preview.changes.removed.one': '1 quitada',
  'preview.changes.reordered': '{n} movidas',
  'preview.changes.reordered.one': '1 movida',
  'preview.changes.summary': 'presentación reescrita',
  'preview.changes.skills': '{n} competencias modificadas',
  'preview.changes.skills.one': '1 competencia modificada',
  'preview.changes.unpaired': 'Las frases traducidas no se pueden comparar una a una: revisa los cambios.',
  'preview.changes.allKept': 'Todas las líneas de tu CV siguen ahí.',
  'preview.changes.lost': '{n} de las {total} líneas de tu CV ya no están.',
  'preview.changes.lost.one': '1 de las {total} líneas de tu CV ya no está.',
  'preview.questions.none': 'Todavía ningún detalle.',
  'preview.numbers.warning': 'Revisa antes de enviar, no está en tu perfil:',
  'letterAngles.title': 'Antes de escribir la carta',
  'letterAngles.intro': 'Elige un enfoque, o dilo con tus palabras.',
  'letterAngles.placeholder': 'Con tus palabras (opcional)',
  'letterAngles.write': 'Escribir la carta',
  'letterAngles.skip': 'Saltar',
  'preview.letter.refine.placeholder': '¿Qué cambiar? (p. ej. más corta, más cálida, insiste en…)',
  'preview.letter.stale': 'Esta carta se escribió para una versión anterior del CV.',
  'preview.letter.stale.regenerate': 'Reescribir la carta',
  'preview.letter.stale.keep': 'Conservarla',
  'preview.settings.makeDefault': 'Usar como predeterminado',
  'preview.letter.writing': 'Escribiendo tu carta…',
  'preview.rate.cta': 'Valorar la extensión',
  'preview.rate.title': '¿Te ayuda? Una valoración en Chrome Web Store nos ayuda muchísimo.',
  'preview.regenerateLetter': 'Regenerar la carta',
  'preview.regenerating': 'Regenerando…',
  'preview.settings.accent': 'Color de acento',
  'preview.settings.apply': 'Rehacer el CV con estos ajustes',
  'preview.settings.applyLetter': 'Regenerar la carta',
  'preview.settings.group.layout': 'Maquetación',
  'preview.settings.group.writing': 'Redacción con IA',
  'preview.settings.hint': 'La maquetación se aplica al momento. Los ajustes de redacción requieren regenerar.',
  'preview.settings.language': 'Idioma',
  'preview.settings.language.auto': 'Auto · inglés por defecto',
  'preview.settings.language.en': 'Inglés',
  'preview.settings.language.fr': 'Francés',
  'preview.settings.language.hint': 'Auto: inglés por defecto; si la oferta está en un idioma que hablas (perfil), el documento sale en ese idioma.',
  'preview.settings.length': 'Longitud',
  'preview.settings.length.1 page': '1 página',
  'preview.settings.length.2 pages': '2 páginas',
  'preview.settings.length.Courte': 'Corta',
  'preview.settings.length.Détaillée': 'Detallada',
  'preview.settings.length.Sans limite': 'Sin límite',
  'preview.settings.length.Standard': 'Estándar',
  'preview.settings.letterHeader': 'Encabezado de la carta',
  'preview.settings.letterHeader.name': 'Nombre como título',
  'preview.settings.letterHeader.none': 'Ninguno',
  'preview.settings.letterHeader.sender': 'Remitente',
  'preview.settings.opt.audacieux.confirm': 'Modo Audaz: la IA podrá afirmar, de forma plausible, competencias que aún no tienes, te toca defenderlas en la entrevista.',
  'preview.settings.opt.audacieux.no': 'Cancelar',
  'preview.settings.opt.audacieux.yes': 'Lo entiendo, activar',
  'preview.settings.opt.Audacieux': 'Audaz',
  'preview.settings.opt.Ciblé': 'Dirigido',
  'preview.settings.opt.Équilibré': 'Equilibrado',
  'preview.settings.opt.Fidèle': 'Fiel',
  'preview.settings.opt.hint.Audacieux': 'Puede afirmar competencias que aún no tienes, te toca defenderlas en la entrevista.',
  'preview.settings.opt.hint.Ciblé': 'Prioriza y acorta para ajustarse a la oferta, sin inventar nada.',
  'preview.settings.opt.hint.Équilibré': 'Reescribe y acorta moderadamente los puntos menos pertinentes.',
  'preview.settings.opt.hint.Fidèle': 'Conserva todo tu contenido, lo reordena para la oferta. Sin pérdidas.',
  'preview.settings.optimization': 'Nivel de adaptación',
  'preview.settings.section.education': 'Formación',
  'preview.settings.section.languages': 'Idiomas',
  'preview.settings.section.projects': 'Proyectos',
  'preview.settings.section.skills': 'Competencias',
  'preview.settings.section.summary': 'Frase de presentación',
  'preview.settings.sections': 'Secciones a incluir',
  'preview.settings.template': 'Plantilla',
  'preview.settings.template.ats': 'ATS',
  'preview.settings.template.modern': 'Moderna',
  'preview.settings.template.default': 'Usar siempre esta plantilla',
  'preview.settings.title': 'Ajustes',
  'preview.settings.toggle': 'Ajustes',
  'preview.settings.tone': 'Tono',
  'preview.settings.tone.Dynamique': 'Dinámico',
  'preview.settings.tone.Exécutif': 'Ejecutivo',
  'preview.settings.tone.hint.Dynamique': 'Verbos de acción, energía, resultados por delante.',
  'preview.settings.tone.hint.Exécutif': 'Vocabulario de liderazgo, dirección, visión estratégica.',
  'preview.settings.tone.hint.Sobre': 'Frases directas y factuales, sin énfasis de marketing.',
  'preview.settings.tone.Sobre': 'Sobrio',
  'preview.tab.cv': 'CV',
  'preview.tab.letter': 'Carta',
  'preview.versions.current': ' (mostrada)',
  'preview.versions.item': 'Versión {n}: {label}',
  'preview.versions.label': 'Versiones',
  'revision.cancel': 'Cerrar',
  'revision.preset.executive': 'Tono ejecutivo',
  'revision.preset.metrics': 'Destacar las cifras',
  'revision.preset.punchy': 'Más contundente',
  'revision.preset.shorter': 'Acortar',
  'revision.error.empty': 'La IA no pudo revisar este fragmento.',
  'revision.placeholder': 'O describe la revisión…',
  'revision.submit': 'OK',
  'revision.title': 'Revisión con IA',
  'revision.trigger': 'Revisar',
}

const LABELS_PAR_LANGUE: Record<Exclude<Lang, 'fr'>, Partial<Record<string, string>>> = { en: LABELS_EN, nl: LABELS_NL, it: LABELS_IT, es: LABELS_ES }

export function useLabels(): TranslateFn {
  const lang = useLang()
  const dict = lang === 'fr' ? undefined : LABELS_PAR_LANGUE[lang]
  return (key, vars) => {
    let out = dict?.[key as keyof typeof LABELS_FR] ?? LABELS_FR[key] ?? key
    if (vars) for (const [k, v] of Object.entries(vars)) out = out.replaceAll(`{${k}}`, String(v))
    return out
  }
}

/**
 * Le vocabulaire du panneau de score et du comparateur (`packages/ui`), dans
 * la langue de l'écran.
 *
 * Ces deux composants prennent leurs libellés en PROP, avec le français pour
 * défaut : sans ces deux hooks, un écran passé en anglais gardait un panneau
 * de score entièrement français au milieu, ce que la vérification Playwright
 * du 2026-08-26 a montré en une capture. Le contenu des dictionnaires vit
 * dans `packages/ui`, pas ici : deux apps qui les recopieraient rouvriraient
 * la divergence que ce paquet existe pour fermer.
 */
export function useScoreLabels(): ScoreLabels {
  return SCORE_LABELS[useLang()]
}

export function useCompareLabels(): CompareLabels {
  return COMPARE_LABELS[useLang()]
}
